/*
 * appraisalFormSchema.js
 * Declarative layout that mirrors the printed "Faculty Appraisal 2025-26"
 * form (8 pages). Each criterion defines:
 *   - the exact table columns from the PDF
 *   - how to map an auto-filled (VERIFIED) evidence row into those columns
 *   - marking-scheme notes shown under the heading
 *
 * The Appraisal page renders this schema so the on-screen form looks like
 * the official document. Evidence rows come from the backend (verified data
 * only). Score inputs (Self / Evaluator) live in the section headers.
 */

const g = (obj, key) => {
  const v = obj ? obj[key] : undefined;
  return v === null || v === undefined || v === "" ? "" : v;
};

// A column: { header, get(row, index) }
const SN = { header: "SN", get: (_row, i) => i + 1, width: 48 };

export const FORM_SCHEMA = {
  A: {
    columns: [
      { header: "Examination", get: (r) => g(r, "qualification_other") || g(r, "qualification_type") },
      { header: "Year of passing", get: (r) => g(r, "year_of_passing") },
      { header: "Name of Institute / University", get: (r) => g(r, "university") },
      { header: "Specialization", get: (r) => g(r, "specialization") },
    ],
  },

  B1: {
    columns: [
      SN,
      { header: "Program Name", get: (r) => g(r, "program_name") },
      { header: "Semester", get: (r) => g(r, "semester") },
      { header: "Name of Subject(s)", get: (r) => g(r, "subject_name") },
      { header: "Subject Category (T/P)", get: (r) => g(r, "subject_category") },
    ],
  },
  B2: {
    note: [
      "For prize winning in Hackathon, Ideathon etc (Rank 1, 2 or 3) - Mentor: 10, Co-Mentor: 05",
      "For Project Competition/Startups/Techno-Social Project outside the institute: Mentor: 05, Co-Mentor: 03",
      "For Participation in Hackathon, Ideathon, Project competition etc - Mentor: 03, Co-Mentor: 02",
    ],
    columns: [
      SN,
      { header: "University / National / International Level", get: (r) => g(r, "level") },
      { header: "Name of organizing Institute/ Industry", get: (r) => g(r, "organizing_institute") || g(r, "institute_name") },
      { header: "Name of Innovative Project", get: (r) => g(r, "project_name") },
      { header: "Mentor / Co-Mentor", get: (r) => g(r, "mentor_role") || g(r, "role") },
      { header: "Rank / Participation", get: (r) => g(r, "rank_awarded") },
    ],
  },
  B3: {
    columns: [
      SN,
      { header: "Name of the Setup", get: (r) => g(r, "setup_name") },
      { header: "Name of Laboratory", get: (r) => g(r, "laboratory_name") },
      { header: "Expenses", get: (r) => g(r, "expenses") },
      { header: "Semester", get: (r) => g(r, "semester") },
    ],
  },
  B4: {
    columns: [
      { header: "Average Feedback Score (scale of 5)", get: (r) => g(r, "feedback_score") },
      { header: "Feedback Score x 4", get: (r) => (Number(g(r, "feedback_score")) ? (Number(g(r, "feedback_score")) * 4).toFixed(2) : "") },
    ],
  },
  B5: {
    note: ["Marking Scheme: >=2 distinct methods -> 03, 01 method -> 02, Otherwise -> 00"],
    columns: [
      SN,
      { header: "Course Name & Code", get: (r) => g(r, "course_name") },
      { header: "Method(s) Used", get: (r) => g(r, "methods_used") },
      { header: "No. of Lectures", get: (r) => g(r, "no_of_lectures") },
      { header: "CO/PO Addressed", get: (r) => g(r, "co_po") },
    ],
    manualRows: 1,
  },
  B6: {
    note: ["Marking Scheme: >=2 cases -> 03, 1 case -> 02, none -> 00"],
    columns: [
      SN,
      { header: "Course Name & Code", get: (r) => g(r, "course_name") },
      { header: "Case Study Title", get: (r) => g(r, "case_title") },
      { header: "Type (Industry / Research / Societal)", get: (r) => g(r, "type") },
      { header: "CO & PO Addressed", get: (r) => g(r, "co_po") },
    ],
    manualRows: 1,
  },
  B7: {
    note: ["Marking Scheme: CEP in >=2 courses (open-ended) -> 02, CEP in 1 course -> 01, No CEP -> 00."],
    columns: [
      SN,
      { header: "Course Name & Code", get: (r) => g(r, "course_name") },
      { header: "CEP Assignment Title", get: (r) => g(r, "title") },
      { header: "Open-ended? (Y/N)", get: (r) => g(r, "open_ended") },
      { header: "SDG Addressed", get: (r) => g(r, "sdg") },
      { header: "CO & PO Addressed", get: (r) => g(r, "co_po") },
    ],
    manualRows: 1,
  },
  B8: { subLabel: "As per Course file index provided by IQAC", academicCoordinator: true },
  B9: { subLabel: "As per Practical file index provided by IQAC", academicCoordinator: true },

  C1: {
    note: [
      "Journal - SCI: 10/paper, SCOPUS: 06/paper, ESCI: 04/paper",
      "Conference (Scopus) - International: 03/Conf (Max 05), National: 02/Conf (Max 03), State: 1/Conf (Max 02)",
      "Books - International: 05/book (Max 10), National: 02/book (Max 04). Book Chapters (Scopus): 02/chapter (Max 06)",
    ],
    groups: [
      {
        title: "Journal",
        filter: (r) => (g(r, "type") || "").toLowerCase().includes("journal") || (!g(r, "type") && !!g(r, "journal_name")),
        columns: [
          SN,
          { header: "Paper Title", get: (r) => g(r, "title") },
          { header: "Journal Title", get: (r) => g(r, "journal_name") },
          { header: "Category (SCI / SCOPUS / ESCI)", get: (r) => g(r, "indexing") || g(r, "category") },
          { header: "Publication Date", get: (r) => g(r, "publication_year") },
        ],
      },
      {
        title: "Conference",
        filter: (r) => (g(r, "type") || "").toLowerCase().includes("conf"),
        columns: [
          SN,
          { header: "Paper Title", get: (r) => g(r, "title") },
          { header: "Name of the Conference", get: (r) => g(r, "journal_name") },
          { header: "Category (SCOPUS / Other)", get: (r) => g(r, "indexing") || g(r, "category") },
          { header: "Conference Date", get: (r) => g(r, "publication_year") },
          { header: "Organizing Institute", get: (r) => g(r, "publisher") },
        ],
      },
      {
        title: "Book / Book Chapter",
        filter: (r) => (g(r, "type") || "").toLowerCase().includes("book"),
        columns: [
          SN,
          { header: "Book / Book Chapter", get: (r) => g(r, "title") },
          { header: "Name of the Publisher", get: (r) => g(r, "publisher") },
          { header: "Category", get: (r) => g(r, "category") },
        ],
      },
    ],
  },
  C2: {
    subLabel: "For PI & Co-PI only",
    note: [
      "Technical Event (STTP/FDP/Conference/MODROBs): <99K:02, 1-1.99L:03, 2-5L:5, >5L:7, >10L:10",
      "Research Funding: <50K:02, 50K-99K:03, 1-1.99L:05, 2-5L:10, >5L:15, >10L:20",
      "Submission of funding proposal: 1 per proposal (Max 3)",
    ],
    columns: [
      SN,
      { header: "Name of Funding Agency", get: (r) => g(r, "agency_name") },
      { header: "Govt. / Private", get: (r) => g(r, "agency_type") },
      { header: "Amount", get: (r) => g(r, "amount") },
    ],
  },
  C3: {
    subLabel: "Applicable to University Approved faculty members only",
    note: ["Registered as Ph.D. supervisor in CAY: 02, Ph.D. completed: 03/student (Max 6), 01 per registration (Max 04)"],
    columns: [
      SN,
      { header: "Name of PhD Scholar", get: (r) => g(r, "scholar_name") },
      { header: "Notification / Registration No. & Date", get: (r) => g(r, "notification_details") },
      { header: "Title of Thesis / Synopsis", get: (r) => g(r, "thesis_title") },
      { header: "Status", get: (r) => g(r, "status") },
    ],
  },
  C4: {
    note: ["Marking Scheme: 01 mark/event, Max 04"],
    columns: [
      SN,
      { header: "Session Chair / Keynote", get: (r) => g(r, "role") },
      { header: "Name of Conference with Institute & NIRF Rank", get: (r) => `${g(r, "name_of_conference")} ${g(r, "institute_name")} ${g(r, "institute_nirf_ranking")}`.trim() },
      { header: "Online / Offline", get: (r) => g(r, "mode") },
      { header: "In India / Abroad", get: (r) => g(r, "level") },
    ],
  },
  C5: {
    subLabel: "Editor / Reviewer of Indexed Journals & Conference (Scopus / WoS) only",
    note: ["Editor: 04, Reviewer of Journal: 02, Reviewer of Conference: 1 (Max 04)"],
    columns: [
      SN,
      { header: "Editor / Reviewer", get: (r) => g(r, "role_type") },
      { header: "Name of Journal", get: (r) => g(r, "journal_name") },
      { header: "International / National", get: (r) => g(r, "level") },
    ],
  },

  D1: {
    note: ["Patents (utility) only. Patent Granted: 10/patent, Patent Published: 03/patent"],
    columns: [
      SN,
      { header: "Title of Patent", get: (r) => g(r, "patent_name") },
      { header: "Filing No.", get: (r) => g(r, "filing_no") },
      { header: "Date", get: (r) => g(r, "date_of_filing") },
      { header: "Published in Journal of IPO (No.)", get: (r) => g(r, "application_no") },
      { header: "Date", get: (r) => g(r, "date_published") },
      { header: "Current Status", get: (r) => g(r, "patent_status") },
    ],
  },
  D2: {
    subLabel: "Industrial Design patent Registration (Registered: 2, Max: 03)",
    columns: [
      SN,
      { header: "Title of Industrial Design", get: (r) => g(r, "design_name") },
      { header: "Filing No.", get: (r) => g(r, "design_number") },
      { header: "Date", get: (r) => g(r, "date_of_grant") },
      { header: "Grant Number", get: (r) => g(r, "design_number") },
    ],
  },
  D3: {
    subLabel: "Copyright (Registered: 01, Max: 02)",
    columns: [
      SN,
      { header: "Title of Copyright", get: (r) => g(r, "copyright_name") },
      { header: "Registration No.", get: (r) => g(r, "status") },
      { header: "Date", get: (r) => g(r, "date_of_registration") },
    ],
  },

  E1: {
    subLabel: "Revenue Generated from Consultancy / Testing from external source. No marks for academic consultancy.",
    note: ["<50K:02, 50K-99K:03, 1-1.99L:05, 2-5L:10, >5L:15"],
    columns: [
      SN,
      { header: "Name of Industry / Organization", get: (r) => g(r, "industry_name") },
      { header: "Consultancy / Testing", get: (r) => g(r, "consultancy_type") },
      { header: "Amount", get: (r) => g(r, "amount") },
    ],
  },

  F1: {
    subLabel: "Certificate course (3 week or 30 Hrs) in Coursera, Edx, Google, Anthropic etc",
    columns: [
      SN,
      { header: "Name of Skill / Tool / Training", get: (r) => g(r, "skill_name") },
      { header: "Domain", get: (r) => g(r, "proficiency_level") },
      { header: "Certifying Agency/Industry", get: (r) => g(r, "certifying_agency") },
      { header: "Duration", get: (r) => g(r, "duration") },
      { header: "Year", get: (r) => g(r, "year") },
    ],
  },
  F2: {
    subLabel: "Offline Industrial Training of more than 30 hrs with recognized agency/industry only.",
    columns: [
      SN,
      { header: "Name of Skill / Tool / Training", get: (r) => g(r, "skill_name") },
      { header: "Domain", get: (r) => g(r, "domain") },
      { header: "Certifying Agency/Industry", get: (r) => g(r, "agency") },
      { header: "Duration", get: (r) => g(r, "duration") },
      { header: "Year", get: (r) => g(r, "year") },
    ],
    manualRows: 1,
  },
  F3: {
    note: ["03 mark/event, Max 05"],
    columns: [
      SN,
      { header: "Name of the Institute", get: (r) => g(r, "institute_name") || g(r, "institute") },
      { header: "Title of the Talk", get: (r) => g(r, "talk_title") || g(r, "title") },
      { header: "Online / Offline", get: (r) => g(r, "mode") },
    ],
  },
  F4: {
    note: ["03 mark/event, Max 05. 3 marks - coordinator & co-coordinator, 1 mark - member"],
    columns: [
      SN,
      { header: "Title of the FDP / STTP / QIP", get: (r) => g(r, "title") },
      { header: "Online / Offline", get: (r) => g(r, "mode") },
      { header: "Sponsored / Self-Financed", get: (r) => g(r, "sponsorship") },
      { header: "Duration with dates", get: (r) => g(r, "duration") },
    ],
  },
  F5: {
    note: ["01 mark/online event, 02 marks/offline event, Max 05"],
    columns: [
      SN,
      { header: "Title of the FDP / STTP / QIP", get: (r) => g(r, "title") },
      { header: "Institute with NIRF Rank", get: (r) => g(r, "institute_name") },
      { header: "Online / Offline", get: (r) => g(r, "mode") },
      { header: "Duration with dates", get: (r) => g(r, "duration") },
    ],
  },
  F6: {
    columns: [
      SN,
      { header: "MOOC / E-Content", get: (r) => g(r, "content_type") },
      { header: "Title of the Course / Content", get: (r) => g(r, "title") },
      { header: "Credits", get: (r) => g(r, "credits") },
      { header: "For which sem. students", get: (r) => g(r, "semester") },
    ],
  },
  F7: {
    note: ["03 mark/certificate, Max 05"],
    columns: [
      SN,
      { header: "Name of Agency", get: (r) => g(r, "agency_name") },
      { header: "Title of the Course", get: (r) => g(r, "course_title") },
      { header: "Topper / Gold / Silver", get: (r) => g(r, "rank") },
      { header: "Grade / %", get: (r) => g(r, "grade_percentage") },
    ],
  },

  G1: {
    subLabel: "International: 05, National: 04, State: 03, University: 02, Max: 05",
    columns: [
      SN,
      { header: "National / International Level", get: (r) => g(r, "level") },
      { header: "Govt. / Recognized Agency", get: (r) => g(r, "organization_type") },
      { header: "Name of Award", get: (r) => g(r, "award_name") },
    ],
  },
  G2: {
    subLabel: "International: 03, National: 02, Max: 05",
    columns: [
      SN,
      { header: "Name of the Professional Society", get: (r) => g(r, "society_name") },
      { header: "Category (Yearly / Life)", get: (r) => g(r, "category") || g(r, "membership_type") },
      { header: "Number", get: (r) => g(r, "membership_number") },
      { header: "Is it Active?", get: (r) => (g(r, "is_active") ? "Yes" : "No") },
    ],
  },

  H: {
    subLabel:
      "Identified new industry for placement -5, for Industrial Internships -3, Coordinated Industrial MoU / supported lab / skill dev program -02. Expert lecture / industrial visit considered in I.1.",
    columns: [
      SN,
      { header: "Activity", get: (r) => g(r, "activity") || "Industry Connect" },
      { header: "Name of the Industry", get: (r) => g(r, "industry_name") },
      { header: "Outcome Expected", get: (r) => g(r, "outcome_expected") },
      { header: "Outcome Achieved", get: (r) => g(r, "outcome_achieved") },
    ],
  },

  I1: {
    note: ["02 per activity as coordinator, 1 per activity as member, Max 05"],
    columns: [
      SN,
      { header: "Activity", get: (r) => g(r, "activity") },
      { header: "Outcome Expected", get: (r) => g(r, "outcome_expected") },
      { header: "Outcome Achieved", get: (r) => g(r, "outcome_achieved") },
    ],
    manualRows: 1,
  },
  I2: {
    columns: [
      SN,
      { header: "Criteria No. (A to H)", get: (r) => g(r, "criteria_no") },
      { header: "Marks scored above Max. Marks of the criteria", get: (r) => g(r, "marks_above") },
    ],
    manualRows: 1,
  },
};

// I.3 Department Level Portfolio parameters (scored by HoD).
export const I3_PARAMETERS = [
  "Timely Completion of Assigned Task (03)",
  "Initiative & adaptability in Work (03)",
  "Teamwork and cooperation (2)",
  "Responsibility & Accountability (3)",
  "Counselling and mentoring of students (2)",
  "Maintaining discipline in the department (2)",
];

// I.4 Institute Level Portfolio level definitions.
export const I4_LEVELS = [
  { level: "Level 1", portfolios: "HoD & Institute level Cell Coordinators", max: 15 },
  { level: "Level 2", portfolios: "CDC, Examination committee, IQAC, R&D, Academic Cell, SADC, Admission Cell members", max: 10 },
  { level: "Level 3", portfolios: "Alumni, Industry-Institute Interaction (III), Discipline, Other cell members", max: 5 },
];

export const I4_PARAMETERS = [
  { name: "Timely Completion of Assigned Tasks", l1: "03", l2: "02", l3: "01" },
  { name: "Initiative & adaptability in Work", l1: "03", l2: "02", l3: "01" },
  { name: "Operational Efficiency - Resource Management & Optimization", l1: "03", l2: "02", l3: "01" },
  { name: "Teamwork and cooperation", l1: "03", l2: "02", l3: "01" },
  { name: "Responsibility & Accountability", l1: "03", l2: "02", l3: "01" },
];

// Summary section rows (A-I).
export const SUMMARY_ROWS = [
  { key: "A", label: "Educational Qualification Up-gradation", max: 10 },
  { key: "B", label: "Outcome-Based Teaching & Learning", max: 50 },
  { key: "C", label: "Research", max: 60 },
  { key: "D", label: "Development Activities & IPR", max: 15 },
  { key: "E", label: "Consultancy & Testing", max: 15 },
  { key: "F", label: "FDPs / Training / Skilling / E-Resource", max: 35 },
  { key: "G", label: "Awards and Recognition / Membership", max: 10 },
  { key: "H", label: "Industry Connect", max: 10 },
  { key: "I", label: "Other Activities", max: 45 },
];
